import _ from 'lodash';
import fp from 'lodash/fp';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import withUser from '@passportinc/hydra-core/components/withUser';
import DynamicString from '@passportinc/hydra-core/components/DynamicString';

import Router from './Routes';
import CartDrawer from './components/Cart/CartDrawer';
import removeFromCart from './redux/actions/removeFromCart';
import changeCartDrawer from './redux/actions/changeCartDrawer';
import { getApplicationById, getSinglePermit } from './services/permits';
import * as constants from './constants';

export const styles = theme => ({
  cartTitle: {
    ...theme.typography.h5,
    fontWeight: 600,
    padding: '0px 24px',
  },
  cartCloseButton: {
    top: 2,
    right: 2,
    color: theme.palette.primary.main,
    fontWeight: 700,
    borderRadius: 24,
    margin: '0 0 0 auto',
    width: 50,
    height: 50,
  },
  cartCloseIconStyle: {
    width: 24,
    height: 24,
  },
  cartRoot: {
    ...theme.mixins.breakpoints.mobile({
      width: '100%',
      height: '100vh',
      margin: 0,
      padding: 0,
      borderRadius: 0,
    }),
  },
});

/**
 * Root level wrapper for routes & header + footer.
 * This includes passing the necessary buttons to the <Header /> component.
 * @returns {React.Component} - The Root Component
 */
export class Root extends React.Component {
  state = {
    isCheckingCart: false,
  };

  /**
   * Sets the document title.
   * @inheritdoc
   * @memberof Root
   */
  componentDidMount() {
    this.maybeClearCart();
    document.title = DynamicString.resolve(
      'hydra_permits_root_site_title',
      '%operator_name% Customer Portal',
    ).replace('%operator_name%', this.props.hydra_operator_name);
  }

  maybeClearCart = async () => {
    const { cart, user, onRemoveCartItem } = this.props;
    this.setState({ isCheckingCart: true });

    try {
      if (user.isLoggedIn && cart.items.length > 0) {
        let invalidCart = false;
        const cartItem = cart.items[0];
        const accountId = _.get(user, 'token["https://api.passportlabs.io"].uid');

        if (cartItem.type === constants.CART_TYPES.application) {
          const application = await getApplicationById(cartItem.id).then(r => r.data);
          if (!application || application.account_id !== accountId) {
            invalidCart = true;
          }
        } else {
          const permit = await getSinglePermit(cartItem.id).then(r => r.data);
          if (!permit || permit.account_id !== accountId) {
            invalidCart = true;
          }
        }

        if (invalidCart) {
          _.map(cart.items, item => onRemoveCartItem({ id: item.id, type: item.type }));
        }
      }
    } catch (e) {
      _.map(cart.items, item => onRemoveCartItem({ id: item.id, type: item.type }));
    }
    this.setState({ isCheckingCart: false });
  }

  /**
   * Opens the cart drawer.
   * @returns {void}
   */
  handleShowCart = () => this.props.onChangeCartDrawer(true);

  /**
   * Closes the cart drawer.
   * @returns {void}
   */
  handleCartDrawerClose = () => this.props.onChangeCartDrawer(false);

  /**
   * @inheritDoc
   */
  render() {
    const {
      classes, cart, settings, onRemoveCartItem, user,
    } = this.props;
    const { isCheckingCart } = this.state;

    return (
      <>
        <Router {..._.omit(this.props, ['classes'])} />
        {user.isLoggedIn && !isCheckingCart && (
          <CartDrawer
            isOpen={!!cart.isDrawerOpen}
            onClose={this.handleCartDrawerClose}
            title={(
              <DynamicString
                lookupPath="hydra_permits_cart_heading"
                defaultText="Your Cart"
              />
            )}
            cart={cart}
            settings={settings}
            classes={{
              root: classes.cartRoot,
              title: classes.cartTitle,
              closeIconStyle: classes.cartCloseIconStyle,
              closeIconButton: classes.cartCloseButton,
              drawerHead: classes.drawerHead,
            }}
            onRemoveCartItem={onRemoveCartItem}
          />
        )}
      </>
    );
  }
}

Root.propTypes = {
  cart: PropTypes.shape({
    isDrawerOpen: PropTypes.bool.isRequired,
    items: PropTypes.array.isRequired,
  }).isRequired,
  settings: PropTypes.shape({}),
  user: PropTypes.shape({}).isRequired,
  classes: PropTypes.shape({}).isRequired,
  actions: PropTypes.shape({}).isRequired,
  hydra_operator_name: PropTypes.string,
  onRemoveCartItem: PropTypes.func.isRequired,
  onChangeCartDrawer: PropTypes.func.isRequired,
  operator: PropTypes.shape({
    contact: PropTypes.object,
  }),
};

Root.defaultProps = {
  operator: {},
  settings: {},
  hydra_operator_name: '',
};

export const mapStateToProps = ({ cart, settings }) => ({
  cart,
  settings: settings.settings,
});

export const mapDispatchToProps = {
  onRemoveCartItem: removeFromCart,
  onChangeCartDrawer: changeCartDrawer,
};

export default fp.compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStyles(styles),
  withUser,
)(Root);
